import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { logError } from '@zg-rentals/log-error';
import { RentalManagerPropertyAddressVerificationWebApiHandler_verification } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../selectors/useSelector';
import AddressActions from '../../actions/AddressActions';

import getLocationRestrictionErrorMessage from '../../utils/getLocationRestrictionErrorMessageUtils';
import accessUtils from '../../utils/accessUtils';

import PropertyConstants from '../../constants/PropertyConstants';
import AddPropertyConstants from '../../constants/AddPropertyConstants';

import type { Dispatch, SetStateAction } from 'react';
import type { ErrorRMWProps } from '../../model/ErrorRMW';

const {
  PROPERTY_TYPES: { COMMUNITY, CONDO },
  LISTING_TYPES: { RENTAL, ROOM },
  LISTING_CODES_BY_TYPE,
  PROPERTY_CODES_BY_TYPE,
  PROPERTY_TYPES_BY_CODE,
} = PropertyConstants;

export const useLocationRestriction = ({
  isLoggedIn,
  updateDuplicateAccountListingId,
  updateErrorObj,
  updateIsLoading,
}: {
  isLoggedIn: boolean;
  updateDuplicateAccountListingId: (arg: string) => void;
  updateErrorObj: (arg: {
    address?: string;
    propertyType?: string | Array<string | JSX.Element>;
    numberOfUnits?: string;
  }) => void;
  updateIsLoading: (arg: boolean) => void;
}) => {
  const [propertyTypeFromApi, updatePropertyTypeFromApi] = useState('');
  const dispatch = useDispatch();

  const { appConfig, user } = useSelector((state) => ({ user: state.user, appConfig: state.app.appConfig }));
  const canPostForSaleListing = accessUtils.canPostForSaleListing({ appConfig });

  async function checkLocationRestriction({
    address,
    unit,
    isRoomForRent,
    propertyType,
    hasAuthCookies,
    numUnitsError = '',
  }: {
    address: string;
    unit: string;
    isRoomForRent: boolean;
    propertyType: keyof typeof PROPERTY_CODES_BY_TYPE;
    hasAuthCookies?: string;
    numUnitsError?: string;
  }) {
    updateIsLoading(true);

    if (!address) {
      updateIsLoading(false);
      return false;
    }

    const getRestrictionsResponse = await (
      isLoggedIn || hasAuthCookies
        ? dispatch(
            AddressActions.getRestrictions({
              address,
              unit,
              listingTypeCode: isRoomForRent ? LISTING_CODES_BY_TYPE[ROOM] : LISTING_CODES_BY_TYPE[RENTAL],
              propertyTypeCode: propertyType ? PROPERTY_CODES_BY_TYPE[propertyType] : null,
            }),
          )
        : dispatch(
            AddressActions.getPublicRestrictions({
              address,
              unit,
              listingTypeCode: isRoomForRent ? LISTING_CODES_BY_TYPE[ROOM] : LISTING_CODES_BY_TYPE[RENTAL],
              propertyTypeCode: propertyType ? PROPERTY_CODES_BY_TYPE[propertyType] : null,
            }),
          )
    )
      // @ts-expect-error
      .catch(() => {
        // call failed, do not update errorObj
        updateIsLoading(false);
      });

    const newErrorObj = {
      address: '',
      propertyType: '',
      numberOfUnits: numUnitsError,
    };

    if (getRestrictionsResponse) {
      const errorMessage = getLocationRestrictionErrorMessage(
        getRestrictionsResponse,
        propertyType === COMMUNITY,
        user.isMultifamilyCustomer,
        false,
        user.isSingleFamilyEnterpriseCustomer,
        user.isPostingPathAdmin,
        user.isHousingConnector,
        canPostForSaleListing,
      );

      if (errorMessage) {
        // if user sees the ErrorMessageConstants.LOCATION_RESTRICTION.MF_REQUIRED message returned by
        // getLocationRestrictionErrorMessage(), then auto set the property to be community, unless
        // the user manually input a unit, which would make it a condo.
        if (getRestrictionsResponse.paidMultifamilyLocation) {
          if (!propertyType) {
            const propertyTypeToShow = unit ? CONDO : COMMUNITY;
            updatePropertyTypeFromApi(propertyTypeToShow);
          }

          newErrorObj.propertyType = errorMessage as string;
          updateErrorObj(newErrorObj);
          updateIsLoading(false);
          return false;
        }

        newErrorObj.address = errorMessage as string;
      }

      // only for logged in users
      if (getRestrictionsResponse.duplicateAccountListingId) {
        updateDuplicateAccountListingId(getRestrictionsResponse.duplicateAccountListingId);
      }
    }

    const getDetailsRes = await dispatch(
      AddressActions.getDetails({ address, unit: propertyType === CONDO ? unit : '' }),
    )
      // @ts-expect-error
      .catch((err: ErrorRMWProps) => {
        logError({
          error: err,
          context: 'PostingSearchModule#useLocationRestriction',
        });
      });

    if (getDetailsRes && getDetailsRes.propertyTypeCode) {
      updatePropertyTypeFromApi(PROPERTY_TYPES_BY_CODE[getDetailsRes.propertyTypeCode]);
      newErrorObj.propertyType = '';
    }

    updateErrorObj(newErrorObj);
    updateIsLoading(false);

    const hasNoErrors = Object.values(newErrorObj).every((key) => !key);
    return hasNoErrors;
  }

  return { checkLocationRestriction, propertyTypeFromApi };
};

export const useValidateForm = (
  updateErrorObj: Dispatch<
    SetStateAction<{
      address?: string;
      propertyType?: string | Array<string | JSX.Element>;
      numberOfUnits?: string;
    }>
  >,
) => {
  async function verifyAddress({ address, unit, errorObj }: { address: string; unit: string; errorObj: object }) {
    try {
      const { isValidatedWithoutUnit } = await RentalManagerPropertyAddressVerificationWebApiHandler_verification({
        fullAddress: address,
        unit,
      });

      // if the property is valid without a unit number
      if (unit !== '' && isValidatedWithoutUnit) {
        updateErrorObj({
          ...errorObj,
          address: `We are unable to validate the
                    property address with this unit number. Please double-check before creating the property.`,
        });
      }

      return true;
    } catch (err) {
      if (err.message) {
        updateErrorObj({
          ...errorObj,
          address: err.message,
        });
      }
      return false;
    }
  }

  const verifyNumUnits = ({ numUnits, errorObj }: { numUnits: string; errorObj: object }) => {
    if (isNaN(Number(numUnits))) {
      updateErrorObj({
        ...errorObj,
        numberOfUnits: 'Please enter a numeric value',
      });
    } else if (String(numUnits).includes('.')) {
      updateErrorObj({
        ...errorObj,
        numberOfUnits: 'Please enter a whole number',
      });
    } else if (Number(numUnits) > AddPropertyConstants.MAX_NUM_UNITS_VALUE) {
      updateErrorObj({
        ...errorObj,
        numberOfUnits: AddPropertyConstants.MAX_NUM_UNITS_ERROR,
      });
    } else {
      updateErrorObj({
        ...errorObj,
        numberOfUnits: '',
      });
    }
  };

  const checkIsFormComplete = ({
    address,
    propertyType,
    numUnits,
    errorObj,
  }: {
    address: string;
    propertyType: string | null;
    numUnits: number | string;
    errorObj: {
      address?: string;
      propertyType?: string | Array<string | JSX.Element>;
      numberOfUnits?: string;
    };
  }) => {
    const newErrorObj = {
      ...errorObj,
      address: address ? errorObj.address : 'Enter an address',
      propertyType: propertyType ? errorObj.propertyType : 'Enter a property type',
      numberOfUnits: !numUnits && propertyType === COMMUNITY ? 'Enter number of units' : errorObj.numberOfUnits,
    };

    updateErrorObj(newErrorObj);

    return Object.values(newErrorObj).every((key) => key === '');
  };

  return { checkIsFormComplete, verifyAddress, verifyNumUnits };
};
